import { useEffect, useState } from 'react';
import { formatDateTime, formatFileSize, type MediaItem } from '@/lib/media';
import { resolveFileKind } from '@/pages/admin/media/components/MediaCard';

interface Props {
  item: MediaItem | null;
  onClose: () => void;
  onDelete: (item: MediaItem) => void;
}

export default function MediaDetailModal({ item, onClose, onDelete }: Props) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setCopied(false);
  }, [item]);

  useEffect(() => {
    if (!item) return undefined;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [item, onClose]);

  if (!item) return null;
  const kind = resolveFileKind(item.fileName || item.key);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(item.url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground-950/40 animate-[fade-in_0.2s_ease-out]" onClick={onClose} />
      <div className="relative flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 animate-[scale-in_0.2s_ease-out]">
        <div className="flex items-center justify-between border-b border-background-200 px-5 py-4">
          <h2 className="font-heading text-sm font-semibold">ファイル詳細</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-200"
            aria-label="閉じる"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-5">
          <div className="flex h-48 items-center justify-center overflow-hidden rounded-md bg-background-100">
            {kind.label === '画像' ? (
              <img src={item.url} alt={item.fileName} className="h-full w-full object-contain" />
            ) : (
              <span className={`flex h-14 w-14 items-center justify-center rounded-lg ${kind.tone}`}>
                <i className={`${kind.icon} text-3xl`} />
              </span>
            )}
          </div>

          <dl className="space-y-2.5">
            <Row label="ファイル名" value={item.fileName} />
            <Row label="種別" value={kind.label} />
            <Row label="サイズ" value={formatFileSize(item.size)} />
            <Row label="更新日時" value={formatDateTime(item.lastModified)} />
          </dl>

          <div>
            <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-foreground-500">
              S3キー
            </p>
            <code className="block break-all rounded-md border border-background-200 bg-background-100 px-3 py-2 text-xs text-foreground-700">
              {item.key}
            </code>
          </div>

          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <p className="text-xs font-medium uppercase tracking-wide text-foreground-500">
                公開URL
              </p>
              <button
                type="button"
                onClick={handleCopy}
                className="flex items-center gap-1 rounded-md border border-background-300 px-2 py-1 text-xs text-foreground-600 transition-colors hover:bg-background-100"
              >
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'} />
                </span>
                {copied ? 'コピーしました' : 'コピー'}
              </button>
            </div>
            <code className="block break-all rounded-md border border-background-200 bg-background-100 px-3 py-2 text-xs text-foreground-700">
              {item.url}
            </code>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-background-200 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100"
          >
            閉じる
          </button>
          <button
            type="button"
            onClick={() => onDelete(item)}
            className="flex items-center gap-2 rounded-md bg-accent-600 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-accent-700"
          >
            <i className="ri-delete-bin-line" />
            削除
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-background-100 pb-2.5 last:border-0">
      <dt className="shrink-0 text-sm text-foreground-500">{label}</dt>
      <dd className="break-all text-right text-sm text-foreground-900">{value}</dd>
    </div>
  );
}